import { Form } from "@/app/global-components/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, CircularProgress } from "@mui/material";
import { useState } from "react";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import usePaylat from "./hook/usePayLater";
import LoginFieldView from "@/pages/login/components/view/login-field-view";
import Footer from "@/layouts/footer";
import NavPage from "@/layouts/nav-header";

const formSchema = z.object({
  name: z.string(),
  email: z.string().email("Invalid email").min(1, "Email is required"),
  phoneNumber: z
    .string()
    .min(10, "Phone number must be at least 10 digits")
    .max(10)
    .optional(),
  company: z.string(),
});
type FormData = z.infer<typeof formSchema>;
function PayLater() {
  const { sendPaylat, isLoading, error } = usePaylat();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });
  const onSubmit = async (data: FormData): Promise<void> => {
    const response = await sendPaylat(data);
    if (response) {
      setIsSubmitted(true);
    }
  };
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-between">
      <NavPage isForm={1}>
        <div className="mt-44 flex flex-col items-center ">
          <h2 className="text-xl md:text-3xl lg:text-3xl font-bold mb-6 text-gray-800">
            {t("payLater.title")}
          </h2>
          {isSubmitted ? (
            <p className="text-green-600 font-semibold text-lg">
              {t("payLater.successMessage")}
            </p>
          ) : (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="w-96 md:w-[600px] lg:w-[700px] rounded-3xl bg-white p-10"
              >
                <LoginFieldView
                  name="name"
                  label={t("item.fields.name")}
                  placeholder={t("item.fields.name")}
                  inputType="text"
                  formControl={form.control}
                />
                <LoginFieldView
                  name="email"
                  label={t("profile.fields.email")}
                  placeholder={t("profile.fields.email")}
                  inputType="email"
                  formControl={form.control}
                />
                <LoginFieldView
                  name="phoneNumber"
                  label={t("profile.fields.phoneNumber")}
                  placeholder={t("profile.fields.phoneNumber")}
                  inputType="text"
                  formControl={form.control}
                />

                <LoginFieldView
                  name="company"
                  label={t("company.info")}
                  placeholder={t("company.info")}
                  inputType="text"
                  multiline
                  formControl={form.control}
                />
                {error && (
                  <p className="text-red-500 mt-2">
                    {t("payLater.errorMessage")}
                  </p>
                )}
                <Button
                  type="submit"
                  className="!mt-10 text !bg-black"
                  variant="contained"
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t("payLater.submit")
                  )}
                </Button>
              </form>
            </Form>
          )}
        </div>
        <Footer />
      </NavPage>
    </div>
  );
}

export default PayLater;
